import React from "react";
import PreOrderTime from "../../SelectedBooking/PreOrders/CustomColumns/PreOrderTime";
import PreOrderItem from "../../SelectedBooking/PreOrders/CustomColumns/PreOrderItem";

export type PreOrderDataItem = {
  timeCol: JSX.Element;
  itemCol: JSX.Element | string;
  name?: string;
  description?: string;
  for_name?: string;
  quantity: number | string;
  priceCol: JSX.Element | string;
  total: string;
  type?: string;
  sub_type?: string;
  time?: string;
  isTypeHeader?: boolean;
  isSubtypeHeader?: boolean;
  isMenuHeader?: boolean;
  isTimeHeader?: boolean;
};

type HeaderType =
  | "isTypeHeader"
  | "isSubtypeHeader"
  | "isMenuHeader"
  | "isTimeHeader";

/**
 * Creates a header item for grouping pre-orders
 */
export const createHeaderItem = (
  subType: string,
  headerType: HeaderType
): PreOrderDataItem => ({
  timeCol: React.createElement(PreOrderTime, { subType, time: "" }),
  itemCol: "",
  quantity: "",
  priceCol: "",
  total: "",
  [headerType]: true
});

/**
 * Groups items by a specific key extractor function
 */
export const groupByKey = <T extends PreOrderDataItem>(
  data: T[],
  keyExtractor: (item: T) => string
): Record<string, T[]> => {
  return data.reduce<Record<string, T[]>>((acc, item) => {
    const key = keyExtractor(item);
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
};

/**
 * Groups pre-order items by type
 */
export const groupByType = (data: PreOrderDataItem[]): PreOrderDataItem[] => {
  const grouped = groupByKey(data, (item) => item.type ?? "Other");

  const result: PreOrderDataItem[] = [];
  Object.entries(grouped).forEach(([type, items]) => {
    result.push(createHeaderItem(`${type} Items`, "isTypeHeader"));
    result.push(...items);
  });

  return result;
};

/**
 * Groups pre-order items by subtype
 */
export const groupBySubtype = (
  data: PreOrderDataItem[]
): PreOrderDataItem[] => {
  const sorted = [...data].sort((a, b) => {
    const subtypeA = a.sub_type ?? "Other";
    const subtypeB = b.sub_type ?? "Other";
    return subtypeA.localeCompare(subtypeB);
  });

  const result: PreOrderDataItem[] = [];
  let currentSubtype: string | null = null;

  sorted.forEach((item) => {
    if (item.sub_type && item.sub_type !== currentSubtype) {
      result.push(createHeaderItem(item.sub_type, "isSubtypeHeader"));
      currentSubtype = item.sub_type;
    }
    result.push(item);
  });

  return result;
};

/**
 * Groups pre-order items by fixed price vs à la carte
 */
export const groupByFixedPrice = (
  data: PreOrderDataItem[]
): PreOrderDataItem[] => {
  const fixedPriceItems: PreOrderDataItem[] = [];
  const regularItems: PreOrderDataItem[] = [];

  data.forEach((item) => {
    if (item.type === "menu" || item.name?.toLowerCase().includes("menu")) {
      fixedPriceItems.push(item);
    } else {
      regularItems.push(item);
    }
  });

  const result: PreOrderDataItem[] = [];
  if (fixedPriceItems.length > 0) {
    result.push(createHeaderItem("Fixed Price Menu Items", "isMenuHeader"));
    result.push(...fixedPriceItems);
  }
  if (regularItems.length > 0) {
    result.push(createHeaderItem("À la Carte Items", "isMenuHeader"));
    result.push(...regularItems);
  }

  return result;
};

/**
 * Determines time group based on hour
 */
export const getTimeGroup = (time: string | undefined): string => {
  if (!time) return "Other";

  const hour = parseInt(time.split(":")[0]);
  if (hour < 12) return "Morning";
  if (hour < 17) return "Afternoon";
  if (hour < 22) return "Evening";
  return "Other";
};

/**
 * Groups pre-order items by time of day
 */
export const groupByTime = (data: PreOrderDataItem[]): PreOrderDataItem[] => {
  const grouped = groupByKey(data, (item) => getTimeGroup(item.time));

  const result: PreOrderDataItem[] = [];
  const timeOrder = ["Morning", "Afternoon", "Evening", "Other"];

  timeOrder.forEach((timeGroup) => {
    if (grouped[timeGroup]) {
      result.push(createHeaderItem(`${timeGroup} Service`, "isTimeHeader"));
      result.push(...grouped[timeGroup]);
    }
  });

  return result;
};

/**
 * Filters and transforms pre-order data based on selected filters
 */
export const filterPreOrdersData = (
  data: PreOrderDataItem[],
  filters: {
    separateByType: boolean;
    separateBySubtype: boolean;
    separateFixedPriceMenuItems: boolean;
    separateByTime: boolean;
    displayForNames: boolean;
  }
): PreOrderDataItem[] => {
  if (!data || data.length === 0) return data;

  let filteredData = [...data];

  if (filters.displayForNames) {
    filteredData = filteredData.map((item) => ({
      ...item,
      itemCol: React.createElement(PreOrderItem, {
        name: item.name ?? "",
        description: item.description ?? "",
        customer: item.for_name ? `For: ${item.for_name}` : ""
      })
    }));
  }

  if (filters.separateByType) {
    filteredData = groupByType(filteredData);
  }

  if (filters.separateBySubtype) {
    filteredData = groupBySubtype(filteredData);
  }

  if (filters.separateFixedPriceMenuItems) {
    filteredData = groupByFixedPrice(filteredData);
  }

  if (filters.separateByTime) {
    filteredData = groupByTime(filteredData);
  }

  return filteredData;
};
